import * as React from 'react';
import styled from 'styled-components';
import Axios from 'axios';
import { JsonObject } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { Input } from 'antd';
import { FroalaTaggedInput } from 'components';
import { replaceMarkupWithTags } from 'utils';

import { User } from '@bb/users';
import { ListingDoc, OrgDoc } from '@bb/domain/dist/schemas';
import {
  Loading,
  ExpansionPanel,
  ShowingAgentsFilter,
} from '../../react-common';
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../../react-common/Modal';
import { AgentGroup, AgentType, Coop, OfferDetails } from './types';

import Alert from './Alert';

type ListingWithUserPopulated = JsonObject<
  Omit<ListingDoc, 'reps' | 'org'> & {
    reps: User[];
    org: OrgDoc;
  }
>;

interface OfferRegistrationModalProps {
  closeModal: () => void;
  onSaveOffer: (
    arg0: AgentGroup,
    arg1: Coop[] | null,
    messageOverride: string | null,
    subjectOverride: string | null,
  ) => void;
  visible: boolean;
  userType?: AgentType;
  listing?: ListingWithUserPopulated;
  details?: OfferDetails;
}

interface OfferRegistrationModalState {
  loading: boolean;
  agentFilter: AgentGroup;
  defaultAgentGroup: AgentGroup;
  defaultMessage: string;
  subject: string;
  defaultSubject: string;
  offerCount: number;
  agents: Coop[];
  initialAgentsCopy: Coop[];
  agentsPanelExpanded: boolean;
  search: string;
}

const initialState: OfferRegistrationModalState = {
  loading: true,
  agentFilter: 'all',
  defaultAgentGroup: 'all',
  defaultMessage: '',
  defaultSubject: '',
  subject: '',
  offerCount: 0,
  agents: [],
  initialAgentsCopy: [],
  agentsPanelExpanded: false,
  search: '',
};

const OfferRegistrationModal: React.FunctionComponent<
  OfferRegistrationModalProps
> = ({ closeModal, onSaveOffer, visible, userType, listing, details }) => {
  const [state, setState] =
    React.useState<OfferRegistrationModalState>(initialState);
  const [registerDisabled, setRegisterDisabled] =
    React.useState<boolean>(false);
  const [froalaMessage, setFroalaMessage] = React.useState<string>('');

  const fetchData = async () => {
    const fetchOfferAgents = async (defaultAgentGroup: AgentGroup) => {
      try {
        const { id: listingId } = listing!;
        if (!listingId) return {};

        const {
          data: { agentsSubscribed, agentsUnsubscribed },
        } = await Axios.get(`/listing/${listingId}/offer_agents`);

        const agents = [...agentsSubscribed, ...agentsUnsubscribed];

        const agentFilter = userType === 'agent' ? 'all' : defaultAgentGroup;

        const processedAgents = agents
          .map((agent: Coop & { id?: string }) => ({
              ...agent,
              checked:
                agentFilter === 'all' ||
                (agentFilter === 'registered' && agent.hasRegisteredOffer),
              uid: agent.uid || agent.id,
            } as Coop))
          .sort(({ name: nameA }, { name: nameB }) => {
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });
        return {
          agents: processedAgents,
          initialAgentsCopy: processedAgents,
        };
      } catch (err) {
        errorReporter.error(`Error fetching offer agent counts: ${err}`);
        return {};
      }
    };

    try {
      const offerDetails = { ...details };
      const offerAgents = await fetchOfferAgents(offerDetails.agentFilter!);
      setFroalaMessage(offerDetails.message!);
      delete offerDetails.message;
      const changes = {
        ...offerDetails,
        ...offerAgents,
      };
      setState({ ...state, ...changes, loading: false });
    } catch (err) {
      errorReporter.error(err);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    setRegisterDisabled(false);
  }, [state]);

  const renderCount = (option: AgentGroup) => {
    if (option !== state.agentFilter) return '';

    let total = 0;

    if (state.agentFilter === 'all') {
      total = state.agents.length;
    }

    if (state.agentFilter === 'agents') {
      total = state.agents.filter(a => !a.isSubscribedFromListing).length;
    }

    if (state.agentFilter === 'registered') {
      total = state.agents.filter(a => a.hasRegisteredOffer).length;
    }

    const checkedCount = state.agents.filter(a => a.checked).length;

    return `(${checkedCount}/${total})`;
  };

  const onExpansionChange = (value: boolean) => {
    let newValue = value;
    if (state.agentFilter === 'none') {
      newValue = false;
    }
    setState({ ...state, agentsPanelExpanded: newValue });
  };

  const changeAgentFilter = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setRegisterDisabled(true);
    const agentFilter = e.target.value as AgentGroup;
    if (agentFilter === 'all') {
      setState({
        ...state,
        agentFilter,
        agents: state.initialAgentsCopy.map(agent => ({ ...agent, checked: true })),
      });
    }
    if (agentFilter === 'agents') {
      setState({
        ...state,
        agentFilter,
        agents: state.initialAgentsCopy
          .filter(a => !a.isSubscribedFromListing)
          .map(agent => ({ ...agent, checked: true })),
      });
    }
    if (agentFilter === 'none') {
      setState({
        ...state,
        agentFilter,
        agentsPanelExpanded: false,
        agents: state.initialAgentsCopy.map(agent => ({ ...agent, checked: false })),
      });
    }
    if (agentFilter === 'registered') {
      setState({
        ...state,
        agentFilter,
        agents: state.initialAgentsCopy
          .filter(({ hasRegisteredOffer }) => hasRegisteredOffer)
          .map(agent => ({
            ...agent,
            checked: Boolean(agent.hasRegisteredOffer),
          })),
      });
    }
  };

  const onSubjectChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setRegisterDisabled(true);
    const subject = e.target.value;
    setState({ ...state, subject });
  };

  const saveOffer = async () => {
    const {
      agents,
      defaultMessage,
      subject,
      defaultSubject,
      defaultAgentGroup,
      agentFilter,
    } = state;

    const messageOverride =
      defaultMessage.trim() !== froalaMessage.trim()
        ? replaceMarkupWithTags(froalaMessage)
        : null;

    const subjectOverride = defaultSubject !== subject ? subject : null;
    let agentGroup: AgentGroup;

    const selectedAgents = agents.filter(agent => agent.checked);

    const agentsWithOffer = agents.filter(agent => agent.hasRegisteredOffer);

    const shouldConvertToRegistered =
      selectedAgents.length === agentsWithOffer.length &&
      selectedAgents.every(agent => agent.hasRegisteredOffer);

    if (agentFilter === 'none') {
      // If agentFilter is none, notify only the LA regardless of the selection
      agentGroup = 'none';
    } else if (agentFilter === 'registered' && shouldConvertToRegistered) {
      // If selection count and offer agent count are both 0, shouldConvertToRegistered is true.
      agentGroup = 'registered';
    } else if (
      agentFilter === 'all' &&
      selectedAgents.length === agents.length
    ) {
      agentGroup = 'all';
    } else {
      agentGroup = 'selected';
    }

    if (agentGroup !== 'selected') {
      agentGroup =
        agentGroup === defaultAgentGroup ? defaultAgentGroup : agentGroup;
      return onSaveOffer(agentGroup, null, messageOverride, subjectOverride);
    }

    const agentsToNotify = agents
      .filter(({ checked }) => checked)
      .map(agent => {
        const newAgent = { ...agent };
        delete newAgent.checked;
        return newAgent;
      });

    return onSaveOffer(
      'selected',
      agentsToNotify,
      messageOverride,
      subjectOverride,
    );
  };

  return (
    <Modal
      show={visible}
      onHide={closeModal}
      className="offer-registration-modal"
    >
      <ModalHeader>
        <ModalTitle className="m-l-md">
          Register Offer - Notification
        </ModalTitle>
      </ModalHeader>
      <ModalBody
        style={{
          maxHeight: '65vh',
          minHeight: '25vh',
          overflowY: 'scroll',
          backgroundColor: 'white',
        }}
      >
        {state.loading ? (
          <Loading size="fa-5x" />
        ) : (
          <>
            <Alert
              userType={userType!}
              offerCount={state.offerCount}
              defaultAgentGroup={state.defaultAgentGroup}
            />
            <ExpansionPanel
              buttonLabel="Edit Recipients"
              value={state.agentsPanelExpanded}
              onChange={onExpansionChange}
              title={<h5>Notification Group</h5>}
              subtitle={
                <div>
                  <select
                    value={state.agentFilter}
                    onChange={changeAgentFilter}
                    style={{
                      fontSize: '15px',
                      borderColor: '#ddd',
                      backgroundColor: 'white',
                    }}
                    data-cy="offer-select-notify-group"
                  >
                    <option value="all">All Agents {renderCount('all')}</option>
                    <option value="agents">
                      Showing Agents {renderCount('agents')}
                    </option>
                    <option value="registered">
                      Agents with Registered Offers {renderCount('registered')}
                    </option>
                    <option value="none">Only Listing Agent</option>
                  </select>
                </div>
              }
            >
              <ShowingAgentsFilter
                onChange={agents => setState({ ...state, agents })}
                agents={state.agents}
                extraFilter={({
                  hasRegisteredOffer,
                  isSubscribedFromListing,
                }: Coop) =>
                  Boolean(
                    state.agentFilter === 'all' ||
                      (state.agentFilter === 'agents' &&
                        !isSubscribedFromListing) ||
                      (state.agentFilter === 'registered' &&
                        hasRegisteredOffer),
                  )
                }
              />
            </ExpansionPanel>
            <ExpansionPanel
              buttonLabel="Edit Template"
              title={<h5>Notification Template</h5>}
            >
              <label style={{ width: '100%', marginBottom: '20px' }}>
                <div>Subject</div>
                <Input value={state.subject} onChange={onSubjectChange} />
              </label>

              <label>Body</label>
              <FroalaWrapper>
                <FroalaTaggedInput
                  model={froalaMessage}
                  onModelChange={setFroalaMessage}
                  templateType="offer"
                  config={{ toolbarSticky: false }}
                />
              </FroalaWrapper>
            </ExpansionPanel>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-default" onClick={closeModal}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn btn-success"
          onClick={saveOffer}
          data-cy="offer-register-after-notify"
          disabled={!state.subject.trim().length || registerDisabled}
        >
          Register Offer
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default OfferRegistrationModal;

const FroalaWrapper = styled.div`
  position: relative;
`;
